import React, { useState } from 'react';
import { Mail, Users, Heart, Sparkles, Bell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const Community = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to Mailchimp
    window.open('https://mailchi.mp/5d23857366a0/dosboldlatinas', '_blank');
    toast({
      title: 'Opening Mailing List',
      description: 'Join our community to stay connected!',
    });
  };

  const benefits = [
    {
      icon: Bell,
      title: 'Episode Updates',
      description: 'Be the first to know when new episodes drop and get exclusive behind-the-scenes content.',
    },
    {
      icon: Sparkles,
      title: 'Exclusive Content',
      description: 'Access special resources, tips, and content created just for our community members.',
    },
    {
      icon: Users,
      title: 'Community Events',
      description: 'Get invites to virtual meetups, live Q&As, and special community gatherings.',
    },
    {
      icon: Heart,
      title: 'Special Offers',
      description: 'Receive early access to merchandise launches and exclusive discounts.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section 
        className="py-20 px-6 text-white relative overflow-hidden"
        style={{ backgroundColor: '#b76e79' }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Join Our Community
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Become part of our growing familia! Stay connected, get exclusive updates, 
            and be the first to know about everything Dos Bold Latinas.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Mailing List Signup */}
          <Card className="border-none shadow-2xl mb-16">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-4xl mb-4">
                Join Our <span style={{ color: '#b76e79' }}>Mailing List</span>
              </CardTitle>
              <p className="text-lg text-gray-600">
                Get the latest updates, exclusive content, and stay connected with our community!
              </p>
            </CardHeader>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <a 
                  href="https://mailchi.mp/5d23857366a0/dosboldlatinas"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    className="text-lg px-12 py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    style={{ backgroundColor: '#b76e79' }}
                  >
                    <Mail className="mr-2" size={24} />
                    Subscribe Now
                  </Button>
                </a>
              </div>
              <p className="text-center text-sm text-gray-500">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">
              What You'll <span style={{ color: '#b76e79' }}>Get</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card 
                    key={index} 
                    className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                  >
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: '#b76e79' }}
                        >
                          <Icon size={24} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                          <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Social Media */}
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white border-none">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold mb-4">
                Follow Us on Social Media
              </h3>
              <p className="text-xl text-gray-300 mb-8">
                Stay connected with us on your favorite platforms @dosboldlatinas
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="https://www.instagram.com/dosboldlatinas/" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-lg px-6 py-6 bg-white/10 text-white border-white hover:bg-white hover:text-black"
                  >
                    Instagram
                  </Button>
                </a>
                <a href="https://www.youtube.com/@dosboldlatinas" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-lg px-6 py-6 bg-white/10 text-white border-white hover:bg-white hover:text-black"
                  >
                    YouTube
                  </Button>
                </a>
                <a href="https://open.spotify.com/show/5ajkirPRCHVOfyAQVCHuIj?si=371c471cd58b410f" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-lg px-6 py-6 bg-white/10 text-white border-white hover:bg-white hover:text-black"
                  >
                    Spotify
                  </Button>
                </a>
                <a href="https://podcasts.apple.com/us/podcast/dos-bold-latinas/id1495602509" target="_blank" rel="noopener noreferrer">
                  <Button 
                    size="lg"
                    variant="outline"
                    className="text-lg px-6 py-6 bg-white/10 text-white border-white hover:bg-white hover:text-black"
                  >
                    Apple Podcasts
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Community;